/**
 * Чистая половина разметки экрана: как называется место и как выглядит выгрузка.
 *
 * Вынесено из `components/Annotator.tsx` не ради слоёв, а ради проверки: сам
 * компонент — обработчики мыши и панель, их тестом не поймать, а вот адрес
 * элемента и формат выгрузки — ровно то, ради чего инструмент существует.
 * Сломайся адрес — замечания станут неадресуемыми, и заметить это можно
 * только по бесполезной выгрузке через неделю.
 */

/** Одно замечание: где сказано, о чём и что именно не так. */
export type Note = {
  id: number;
  page: string;
  anchor: string;
  text: string;
  comment: string;
};

/**
 * Адрес элемента — путь по ближайшим `data-annot` вверх по дереву.
 *
 * Три звена, не больше: «товары › строка 14623 › Готово» читается, а
 * пятизвенная цепочка — уже нет. Без единой метки остаётся тег с двумя
 * классами: это ХУДШИЙ случай, а не рабочий, и выглядит он соответственно —
 * увидев его в выгрузке, метку ставят.
 */
export function anchorOf(el: HTMLElement): string {
  const parts: string[] = [];
  let node: HTMLElement | null = el;
  while (node !== null && parts.length < 3) {
    const mark = node.dataset?.["annot"];
    if (mark !== undefined && mark !== "") parts.unshift(mark);
    node = node.parentElement;
  }
  if (parts.length > 0) return parts.join(" › ");
  const cls = (el.getAttribute("class") ?? "")
    .split(/\s+/)
    .slice(0, 2)
    .join(".");
  return el.tagName.toLowerCase() + (cls ? `.${cls}` : "");
}

/** Что на элементе написано — по этому его узнают глазами. */
export function textOf(el: HTMLElement): string {
  return (el.innerText ?? "").replace(/\s+/g, " ").trim().slice(0, 80);
}

/**
 * Разметка одним куском текста, готовым к вставке в сессию.
 *
 * `origin` параметром, а не из `window`: функция обязана быть чистой, иначе её
 * нельзя проверить. Адрес страницы печатается один раз на группу — замечания
 * к одному экрану идут подряд, и повторять ссылку у каждого значило бы
 * утопить в ней текст.
 */
export function asClipboard(
  notes: Note[],
  origin: string,
  now: Date = new Date(),
): string {
  const stamp = now.toLocaleString("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
  const lines = [`Разметка wms · ${stamp}`, ""];
  let page = "";
  notes.forEach((n, i) => {
    if (n.page !== page) {
      page = n.page;
      lines.push(`${origin}${page}`);
    }
    lines.push(`${i + 1}. [${n.anchor}] «${n.text}» → ${n.comment}`);
  });
  return lines.join("\n");
}
